import axios, { type InternalAxiosRequestConfig } from "axios";
import { API_URL } from "@/lib/constants";
import { redirectToLogin } from "@/stores/auth-store";

export const api = axios.create({
	baseURL: `${API_URL}/api/v3`,
	withCredentials: true,
	headers: {
		"Content-Type": "application/json",
	},
});

// ── Refresh single-flight ────────────────────────────────────────
// Múltiplas requests recebendo 401 ao mesmo tempo compartilham uma única
// chamada a POST /api/v3/app-refresh/. A rota usa o cookie HttpOnly
// `app_refresh` e atualiza ambos os cookies via Set-Cookie. Usa-se axios
// "puro" (não a instância `api`) para que a própria chamada de refresh
// não passe pelo response interceptor.
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
	if (!refreshInFlight) {
		refreshInFlight = axios
			.post(`${API_URL}/api/v3/app-refresh/`, null, {
				withCredentials: true,
			})
			.then(() => true)
			.catch(() => false)
			.finally(() => {
				refreshInFlight = null;
			});
	}
	return refreshInFlight;
}

interface RetriableConfig extends InternalAxiosRequestConfig {
	_retried?: boolean;
}

// Rotas que respondem 401 por razões NÃO relacionadas à autenticação
// (ex.: regra de negócio do backend). Para essas, não tentamos refresh
// nem redirecionamos — apenas propagamos o erro para o caller tratar.
const SILENT_401_ROUTES = [
	"/nps-applications/",
	"/student/current_school_class/",
	"/student/colleagues/",
	"/applications/",
];

function isSilent401Url(url: string): boolean {
	return SILENT_401_ROUTES.some((route) => url.includes(route));
}

// ── Response Interceptor ─────────────────────────────────────────
// 401 → tenta refresh uma única vez. Sucesso re-executa a request original
// com os cookies novos; falha redireciona ao login do Django.
// Rotas em SILENT_401_ROUTES pulam o ciclo inteiro (sem refresh, sem redirect).
api.interceptors.response.use(
	(response) => response,
	async (error) => {
		if (!axios.isAxiosError(error) || error.response?.status !== 401) {
			return Promise.reject(error);
		}
		const config = error.config as RetriableConfig | undefined;
		if (config && isSilent401Url(config.url ?? "")) {
			return Promise.reject(error);
		}
		if (!config || config._retried) {
			redirectToLogin();
			return Promise.reject(error);
		}
		config._retried = true;

		const ok = await refreshSession();
		if (!ok) {
			redirectToLogin();
			return Promise.reject(error);
		}
		return api(config);
	},
);
